import Template from "../../components/Template";
import React, { useState, useEffect } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx-js-style";
import { useAuth } from '../../context/AuthContext';
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb } from "../../utils/masks";
import logoEmpresa from "../../components/logoEmpresaBase64";
import patrimonioService from "../../services/patrimonioService";
import { TIPOS_PATRIMONIO, SITUACOES_PATRIMONIO, getTipoLabel, getSituacaoInfo, getLocalizacaoLabel } from "../patrimonio/patrimonioConstants";

const RelatorioInventario = () => {
    const { auth } = useAuth();
    const [dados, setDados] = useState(null); // { records, totais, porLocal, porTipo, porCategoria, porSituacao }
    const [locais, setLocais] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [obras, setObras] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [message, setMessage] = useState("");

    const [filters, setFilters] = useState({
        search: "",
        localId: "",
        obraId: "",
        categoriaId: "",
        tipo: "",
        situacao: "",
        responsavel: "",
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [locaisRes, categoriasRes, obrasRes] = await Promise.all([
                    patrimonioService.listarLocais(),
                    patrimonioService.listarCategorias(),
                    api.get("/obras/listar/todas"),
                ]);
                setLocais(locaisRes || []);
                setCategorias(categoriasRes || []);
                setObras(obrasRes.data || []);
            } catch (error) {
                console.error("Erro ao carregar dados iniciais:", error);
            }
        };
        fetchInitialData();
        fetchRelatorio(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRelatorio = async (currentFilters) => {
        setIsLoading(true);
        try {
            const data = await patrimonioService.relatorioInventario(currentFilters);
            setDados(data);
        } catch (error) {
            console.error("Erro ao buscar relatório de inventário:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar relatório de inventário.");
            setShowDialog(true);
            setDados(null);
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilters = () => fetchRelatorio(filters);

    const clearFilters = () => {
        const emptyFilters = { search: "", localId: "", obraId: "", categoriaId: "", tipo: "", situacao: "", responsavel: "" };
        setFilters(emptyFilters);
        fetchRelatorio(emptyFilters);
    };

    // Descrição dos filtros aplicados (usada nas exportações)
    const getFilterLines = () => {
        const lines = [];
        if (filters.search) lines.push(`Busca: ${filters.search}`);
        if (filters.localId) {
            const local = locais.find(l => l.id === parseInt(filters.localId));
            lines.push(`Local: ${local?.nome || 'N/A'}`);
        }
        if (filters.obraId) {
            const obra = obras.find(o => o.id === parseInt(filters.obraId));
            lines.push(`Obra: ${obra?.nomeProjeto || obra?.nome || 'N/A'}`);
        }
        if (filters.categoriaId) {
            const categoria = categorias.find(c => c.id === parseInt(filters.categoriaId));
            lines.push(`Categoria: ${categoria?.nome || 'N/A'}`);
        }
        if (filters.tipo) lines.push(`Tipo: ${getTipoLabel(filters.tipo)}`);
        if (filters.situacao) lines.push(`Situação: ${getSituacaoInfo(filters.situacao).label}`);
        if (filters.responsavel) lines.push(`Responsável: ${filters.responsavel}`);
        return lines;
    };

    const COLUNAS = [
        { id: 'codigo', label: 'Código', getValue: (p) => p.codigo || '' },
        { id: 'nome', label: 'Nome', getValue: (p) => p.nome || '' },
        { id: 'tipo', label: 'Tipo', getValue: (p) => getTipoLabel(p.tipo) },
        { id: 'categoria', label: 'Categoria', getValue: (p) => p.categoria?.nome || '-' },
        { id: 'localizacao', label: 'Localização', getValue: (p) => getLocalizacaoLabel(p) },
        { id: 'responsavel', label: 'Responsável', getValue: (p) => p.responsavel || '-' },
        { id: 'situacao', label: 'Situação', getValue: (p) => getSituacaoInfo(p.situacao).label },
        { id: 'dataAquisicao', label: 'Aquisição', getValue: (p) => p.dataAquisicao ? formatDateWithoutHour(p.dataAquisicao) : '-' },
        { id: 'valorAquisicao', label: 'Valor Aquisição', getValue: (p) => p.valorAquisicao ?? 0, isCurrency: true },
        { id: 'valorAtual', label: 'Valor Atual', getValue: (p) => p.valorAtual ?? 0, isCurrency: true },
    ];

    // ------------------------------------------------------------------
    // Exportações
    // ------------------------------------------------------------------

    const exportarPDF = () => {
        const records = dados?.records || [];
        if (records.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowDialog(true);
            return;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const pageHeight = doc.internal.pageSize.height;

        // Espaço reservado no fim de cada página para o rodapé (emitido por / logo / paginação)
        const RODAPE_ALTURA = 24;
        const margemTabela = { left: 6, right: 6, bottom: RODAPE_ALTURA };

        // Posiciona o título de uma seção; quebra página se não houver espaço para título + começo da tabela
        const iniciarSecao = (titulo) => {
            let y = doc.lastAutoTable.finalY + 10;
            if (y + 30 > pageHeight - RODAPE_ALTURA) {
                doc.addPage();
                y = 20;
            }
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.text(titulo, 14, y);
            return y + 2;
        };

        doc.setFontSize(16);
        doc.text("Relatório de Inventário de Patrimônio", 14, 20);
        doc.setFontSize(10);

        let yPos = 28;
        getFilterLines().forEach((line) => {
            doc.text(line, 14, yPos);
            yPos += 6;
        });

        const head = [COLUNAS.map(c => c.label)];
        const body = records.map(p => COLUNAS.map(col =>
            col.isCurrency ? formatCurrencyFromDb(col.getValue(p)) : col.getValue(p)
        ));

        // Linha de total
        const totalRow = Array(COLUNAS.length).fill('');
        const idxValorAquisicao = COLUNAS.findIndex(c => c.id === 'valorAquisicao');
        totalRow[idxValorAquisicao - 1] = { content: 'TOTAL:', styles: { fontStyle: 'bold', halign: 'right' } };
        totalRow[idxValorAquisicao] = { content: formatCurrencyFromDb(dados.totais.valorAquisicao), styles: { fontStyle: 'bold' } };
        totalRow[idxValorAquisicao + 1] = { content: formatCurrencyFromDb(dados.totais.valorAtual), styles: { fontStyle: 'bold' } };
        body.push(totalRow);

        const columnStyles = COLUNAS.reduce((acc, col, idx) => {
            acc[idx] = { overflow: 'linebreak', ...(col.isCurrency ? { halign: 'right', cellWidth: 26 } : {}) };
            return acc;
        }, {});

        autoTable(doc, {
            startY: yPos + 2,
            head,
            body,
            margin: margemTabela,
            headStyles: { fillColor: [30, 120, 180], fontSize: 8, halign: 'left' },
            bodyStyles: { valign: 'middle' },
            styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
            columnStyles,
        });

        // Resumo por localização
        autoTable(doc, {
            startY: iniciarSecao("Resumo por Localização"),
            head: [["Localização", "Quantidade", "Valor Aquisição", "Valor Atual"]],
            body: (dados.porLocal || []).map(g => [
                g.nome,
                g.quantidade,
                formatCurrencyFromDb(g.valorAquisicao),
                formatCurrencyFromDb(g.valorAtual),
            ]),
            margin: margemTabela,
            headStyles: { fillColor: [30, 120, 180], fontSize: 8, halign: 'left' },
            styles: { fontSize: 8, cellPadding: 1.5 },
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });

        // Resumo por tipo
        autoTable(doc, {
            startY: iniciarSecao("Resumo por Tipo"),
            head: [["Tipo", "Quantidade", "Valor Aquisição", "Valor Atual"]],
            body: (dados.porTipo || []).map(g => [
                getTipoLabel(g.nome),
                g.quantidade,
                formatCurrencyFromDb(g.valorAquisicao),
                formatCurrencyFromDb(g.valorAtual),
            ]),
            margin: margemTabela,
            headStyles: { fillColor: [30, 120, 180], fontSize: 8, halign: 'left' },
            styles: { fontSize: 8, cellPadding: 1.5 },
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });

        // Resumo por categoria
        autoTable(doc, {
            startY: iniciarSecao("Resumo por Categoria"),
            head: [["Categoria", "Quantidade", "Valor Aquisição", "Valor Atual"]],
            body: (dados.porCategoria || []).map(g => [
                g.nome,
                g.quantidade,
                formatCurrencyFromDb(g.valorAquisicao),
                formatCurrencyFromDb(g.valorAtual),
            ]),
            margin: margemTabela,
            headStyles: { fillColor: [30, 120, 180], fontSize: 8, halign: 'left' },
            styles: { fontSize: 8, cellPadding: 1.5 },
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });

        // Rodapé
        const pageCount = doc.internal.getNumberOfPages();
        const dataEmissao = new Date().toLocaleString('pt-BR');
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.text(`Emitido por: ${auth.user.name || auth.user.nome || "Sistema"}`, 14, doc.internal.pageSize.height - 15);
            doc.text(`Data/Hora: ${dataEmissao}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
            if (logoEmpresa) {
                const logoWidth = 40, logoHeight = 12, pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoWidth) / 2, doc.internal.pageSize.height - 18, logoWidth, logoHeight);
            }
        }

        doc.save("relatorio_inventario.pdf");
    };

    const exportarExcel = () => {
        const records = dados?.records || [];
        if (records.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowDialog(true);
            return;
        }

        const borderThin = { style: 'thin', color: { rgb: 'B0B0B0' } };
        const borders = { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin };

        const titleStyle = {
            font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: borders,
        };
        const filterStyle = {
            font: { italic: true, sz: 11, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: 'DCE6F1' } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: borders,
        };
        const headerStyle = {
            font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '2E75B6' } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: borders,
        };
        const cellStyle = (even) => ({
            font: { sz: 10, color: { rgb: '333333' } },
            fill: { fgColor: { rgb: even ? 'F2F7FB' : 'FFFFFF' } },
            alignment: { vertical: 'center' },
            border: borders,
        });
        const currencyStyle = (even) => ({
            ...cellStyle(even),
            alignment: { vertical: 'center', horizontal: 'right' },
            numFmt: 'R$ #,##0.00',
        });
        const totalStyle = {
            font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '1F4E79' } },
            alignment: { horizontal: 'right', vertical: 'center' },
            border: borders,
        };

        const colCount = COLUNAS.length;
        const aoa = [];
        const merges = [];
        let rowIndex = 0;

        // Título
        aoa.push([{ v: "Relatório de Inventário de Patrimônio", s: titleStyle }, ...Array(colCount - 1).fill({ v: "", s: titleStyle })]);
        merges.push({ s: { r: rowIndex, c: 0 }, e: { r: rowIndex, c: colCount - 1 } });
        rowIndex++;

        // Filtros
        getFilterLines().forEach((line) => {
            aoa.push([{ v: line, s: filterStyle }, ...Array(colCount - 1).fill({ v: "", s: filterStyle })]);
            merges.push({ s: { r: rowIndex, c: 0 }, e: { r: rowIndex, c: colCount - 1 } });
            rowIndex++;
        });

        // Cabeçalho
        aoa.push(COLUNAS.map(c => ({ v: c.label, s: headerStyle })));
        rowIndex++;

        // Linhas
        records.forEach((p, i) => {
            const even = i % 2 === 0;
            aoa.push(COLUNAS.map((col) => {
                const value = col.getValue(p);
                if (col.isCurrency) {
                    return { v: Number(value) || 0, t: 'n', s: currencyStyle(even) };
                }
                return { v: value, s: cellStyle(even) };
            }));
            rowIndex++;
        });

        // Total
        const totalRow = COLUNAS.map((col) => {
            if (col.id === 'valorAquisicao') return { v: dados.totais.valorAquisicao, t: 'n', s: { ...totalStyle, numFmt: 'R$ #,##0.00' } };
            if (col.id === 'valorAtual') return { v: dados.totais.valorAtual, t: 'n', s: { ...totalStyle, numFmt: 'R$ #,##0.00' } };
            if (col.id === 'dataAquisicao') return { v: 'TOTAL:', s: totalStyle };
            return { v: '', s: totalStyle };
        });
        aoa.push(totalRow);
        rowIndex++;

        // Resumo por localização
        aoa.push(Array(colCount).fill({ v: "" }));
        rowIndex++;
        aoa.push([{ v: "Resumo por Localização", s: titleStyle }, ...Array(colCount - 1).fill({ v: "", s: titleStyle })]);
        merges.push({ s: { r: rowIndex, c: 0 }, e: { r: rowIndex, c: colCount - 1 } });
        rowIndex++;
        aoa.push([
            { v: "Localização", s: headerStyle },
            { v: "Quantidade", s: headerStyle },
            { v: "Valor Aquisição", s: headerStyle },
            { v: "Valor Atual", s: headerStyle },
            ...Array(colCount - 4).fill({ v: "", s: headerStyle }),
        ]);
        rowIndex++;
        (dados.porLocal || []).forEach((g, i) => {
            const even = i % 2 === 0;
            aoa.push([
                { v: g.nome, s: cellStyle(even) },
                { v: g.quantidade, t: 'n', s: cellStyle(even) },
                { v: g.valorAquisicao, t: 'n', s: currencyStyle(even) },
                { v: g.valorAtual, t: 'n', s: currencyStyle(even) },
                ...Array(colCount - 4).fill({ v: "", s: cellStyle(even) }),
            ]);
            rowIndex++;
        });

        const worksheet = XLSX.utils.aoa_to_sheet(aoa);
        worksheet['!merges'] = merges;
        worksheet['!cols'] = COLUNAS.map((col) => ({
            wch: col.id === 'nome' || col.id === 'localizacao' ? 34 : 18,
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inventário");
        XLSX.writeFile(workbook, "relatorio_inventario.xlsx");
    };

    const inputClass = "w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-sm text-slate-700";
    const labelClass = "text-slate-700 text-sm font-medium leading-tight";

    const records = dados?.records || [];
    const totais = dados?.totais || { quantidade: 0, valorAquisicao: 0, valorAtual: 0 };

    return (
        <Template>
            <div className="h-auto w-full p-3 sm:p-4 md:p-8 flex flex-col gap-4 md:gap-6">
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <div className="text-stone-800 text-lg sm:text-xl md:text-2xl font-medium font-['Inter']">
                        Relatório de Inventário de Patrimônio
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            className="px-3 py-2 bg-white rounded-md border border-stone-600 flex items-center gap-2"
                            onClick={exportarPDF}
                        >
                            <img className="w-4 h-4" src="/images/icon/pdf.png" alt="PDF" />
                            <span className="text-stone-600 text-sm font-medium hidden sm:block">PDF</span>
                        </button>
                        <button
                            type="button"
                            className="px-3 py-2 bg-white rounded-md border border-stone-600 flex items-center gap-2"
                            onClick={exportarExcel}
                        >
                            <img className="w-4 h-4" src="/images/icon/upload.png" alt="Excel" />
                            <span className="text-stone-600 text-sm font-medium hidden sm:block">Excel</span>
                        </button>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Busca livre</label>
                            <input
                                type="text"
                                className={inputClass}
                                placeholder="Código, nome, nº série, marca..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                onKeyPress={(e) => { if (e.key === 'Enter') applyFilters(); }}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Local</label>
                            <select className={inputClass} value={filters.localId} onChange={(e) => setFilters({ ...filters, localId: e.target.value })}>
                                <option value="">Todos</option>
                                {locais.map((l) => (
                                    <option key={l.id} value={l.id}>{l.nome}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Obra</label>
                            <select className={inputClass} value={filters.obraId} onChange={(e) => setFilters({ ...filters, obraId: e.target.value })}>
                                <option value="">Todas</option>
                                {obras.map((o) => (
                                    <option key={o.id} value={o.id}>{o.nome || o.nomeProjeto}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Tipo</label>
                            <select
                                className={inputClass}
                                value={filters.tipo}
                                onChange={(e) => setFilters({ ...filters, tipo: e.target.value, categoriaId: "" })}
                            >
                                <option value="">Todos</option>
                                {TIPOS_PATRIMONIO.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Categoria</label>
                            <select className={inputClass} value={filters.categoriaId} onChange={(e) => setFilters({ ...filters, categoriaId: e.target.value })}>
                                <option value="">Todas</option>
                                {categorias
                                    .filter((c) => !filters.tipo || c.tipo === filters.tipo)
                                    .map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {filters.tipo ? c.nome : `${getTipoLabel(c.tipo)} - ${c.nome}`}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Situação</label>
                            <select className={inputClass} value={filters.situacao} onChange={(e) => setFilters({ ...filters, situacao: e.target.value })}>
                                <option value="">Todas</option>
                                {SITUACOES_PATRIMONIO.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className={labelClass}>Responsável</label>
                            <input
                                type="text"
                                className={inputClass}
                                placeholder="Nome do responsável"
                                value={filters.responsavel}
                                onChange={(e) => setFilters({ ...filters, responsavel: e.target.value })}
                                onKeyPress={(e) => { if (e.key === 'Enter') applyFilters(); }}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            className="px-4 py-2 bg-white rounded-md border border-stone-500 flex items-center gap-2"
                            onClick={clearFilters}
                        >
                            <img className="w-4 h-4" src="/images/icon/eraser.png" alt="Limpar" />
                            <span className="text-stone-500 text-sm font-medium">Limpar</span>
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 rounded-md flex items-center gap-2"
                            onClick={applyFilters}
                            disabled={isLoading}
                        >
                            <img className="w-4 h-4" src="/images/icon/filter-white.png" alt="Filtrar" />
                            <span className="text-white text-sm font-medium">{isLoading ? "Buscando..." : "Filtrar"}</span>
                        </button>
                    </div>
                </div>

                {/* Cards de totais */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-1">
                        <div className="text-slate-500 text-sm">Quantidade de bens</div>
                        <div className="text-stone-800 text-2xl font-semibold">{totais.quantidade}</div>
                    </div>
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-1">
                        <div className="text-slate-500 text-sm">Valor total de aquisição</div>
                        <div className="text-stone-800 text-2xl font-semibold">{formatCurrencyFromDb(totais.valorAquisicao) || "R$ 0,00"}</div>
                    </div>
                    <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-1">
                        <div className="text-slate-500 text-sm">Valor atual (depreciado)</div>
                        <div className="text-stone-800 text-2xl font-semibold">{formatCurrencyFromDb(totais.valorAtual) || "R$ 0,00"}</div>
                    </div>
                </div>

                {/* Tabela principal */}
                <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">Bens do Inventário</div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-stone-200">
                                    {COLUNAS.map((col) => (
                                        <th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-sm font-bold">
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {records.length === 0 ? (
                                    <tr>
                                        <td colSpan={COLUNAS.length} className="px-4 py-8 text-center text-slate-500 text-sm">
                                            {isLoading ? "Carregando..." : "Nenhum bem encontrado com os filtros aplicados."}
                                        </td>
                                    </tr>
                                ) : (
                                    records.map((p) => (
                                        <tr key={p.id} className="border-b">
                                            {COLUNAS.map((col) => (
                                                <td key={`${p.id}-${col.id}`} className={`px-4 py-2 border border-slate-200 text-slate-700 text-sm ${col.isCurrency ? "text-right" : ""}`}>
                                                    {col.isCurrency ? formatCurrencyFromDb(col.getValue(p)) : col.getValue(p)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            {records.length > 0 && (
                                <tfoot>
                                    <tr className="bg-stone-100 font-bold">
                                        <td colSpan={COLUNAS.length - 2} className="px-4 py-2 border border-slate-200 text-right text-sm">TOTAL:</td>
                                        <td className="px-4 py-2 border border-slate-200 text-right text-sm">{formatCurrencyFromDb(totais.valorAquisicao)}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-right text-sm">{formatCurrencyFromDb(totais.valorAtual)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                {/* Resumos */}
                {records.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                            <div className="text-stone-600 text-base font-medium">Resumo por Localização</div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-stone-100">
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Localização</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Qtde</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Valor Aquisição</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Valor Atual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dados.porLocal || []).map((g) => (
                                            <tr key={g.nome} className="border-b">
                                                <td className="px-4 py-2 border border-slate-200 text-sm">{g.nome}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{g.quantidade}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{formatCurrencyFromDb(g.valorAquisicao)}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{formatCurrencyFromDb(g.valorAtual)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                            <div className="text-stone-600 text-base font-medium">Resumo por Categoria</div>
                            <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                <table className="min-w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-stone-100">
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Categoria</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Qtde</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Valor Aquisição</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Valor Atual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dados.porCategoria || []).map((g) => (
                                            <tr key={g.nome} className="border-b">
                                                <td className="px-4 py-2 border border-slate-200 text-sm">{g.nome}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{g.quantidade}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{formatCurrencyFromDb(g.valorAquisicao)}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{formatCurrencyFromDb(g.valorAtual)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-md border border-slate-200 flex flex-col gap-4">
                            <div className="text-stone-600 text-base font-medium">Resumo por Tipo e Situação</div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-stone-100">
                                            <th className="px-4 py-2 border border-slate-200 text-sm">Tipo</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Qtde</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Valor Aquisição</th>
                                            <th className="px-4 py-2 border border-slate-200 text-sm text-right">Valor Atual</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dados.porTipo || []).map((g) => (
                                            <tr key={g.nome} className="border-b">
                                                <td className="px-4 py-2 border border-slate-200 text-sm">{getTipoLabel(g.nome)}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{g.quantidade}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{formatCurrencyFromDb(g.valorAquisicao)}</td>
                                                <td className="px-4 py-2 border border-slate-200 text-sm text-right">{formatCurrencyFromDb(g.valorAtual)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(dados.porSituacao || []).map((g) => {
                                    const info = getSituacaoInfo(g.nome);
                                    return (
                                        <span
                                            key={g.nome}
                                            style={{
                                                padding: "4px 12px",
                                                borderRadius: "12px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                backgroundColor: info.color,
                                                color: "#FFFFFF",
                                            }}
                                        >
                                            {info.label}: {g.quantidade}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    isVisible={showDialog}
                    message={message}
                    onClose={() => setShowDialog(false)}
                />
            </div>
        </Template>
    );
};

export default RelatorioInventario;
